import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Loader2, X } from "lucide-react";

/**
 * Modal confirmation dialog. Replaces native window.confirm() with a styled,
 * keyboard-friendly popup that matches the rest of the app.
 *
 * Props:
 *   open       boolean
 *   title      string
 *   body       node | string
 *   confirmLabel  string  default "Confirm"
 *   cancelLabel   string  default "Cancel"
 *   variant       "danger" | "default"
 *   loading       boolean
 *   onConfirm     () => void
 *   onCancel      () => void
 */
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  loading = false,
  onConfirm,
  onCancel,
}) {
  const danger = variant === "danger";
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="confirm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-[#0B1038]/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={onCancel}
        >
          <motion.div
            initial={{ y: 16, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 8, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white rounded-md border border-gray-200 shadow-xl overflow-hidden"
          >
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              className="absolute top-3 right-3 p-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div
                  className={`shrink-0 w-10 h-10 rounded-full inline-flex items-center justify-center ${
                    danger
                      ? "bg-rose-50 text-rose-600"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-xl text-[#0B1038] tracking-tight">
                    {title}
                  </h3>
                  {body && (
                    <div className="mt-2 text-sm text-gray-600 leading-relaxed">
                      {body}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="px-4 py-2 rounded-sm border border-gray-300 text-sm font-semibold hover:bg-gray-50 disabled:opacity-60"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-semibold text-white disabled:opacity-60 ${
                    danger
                      ? "bg-rose-600 hover:bg-rose-700"
                      : "bg-[#0B1038] hover:bg-[#151c54]"
                  }`}
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
